import { COMMON_CACHE_KEYS } from "@serophin/shared";
import { cache } from "./cache";

export type Theme = "system" | "light" | "dark";

export const getStoredTheme = (): Theme => {
  if (typeof window === "undefined") {
    return "system";
  }
  return cache.get<Theme>(COMMON_CACHE_KEYS.theme) ?? "system";
};

export const applyTheme = (theme?: Theme): void => {
  const effectiveTheme = theme ?? getStoredTheme();
  const root = document.documentElement;
  const isDark = effectiveTheme === "dark" || (effectiveTheme !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);

  root.classList.toggle("dark", isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
};

const listeners = new Set<(theme: Theme) => void>();

export const subscribeToTheme = (listener: (theme: Theme) => void): (() => void) => {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
};

export const setTheme = (theme: Theme): void => {
  cache.set(COMMON_CACHE_KEYS.theme, theme);
  applyTheme(theme);
  for (const listener of listeners) {
    listener(theme);
  }
};

export const isDarkMode = (): boolean => {
  return document.documentElement.classList.contains("dark");
};

export const watchSystemTheme = (): void => {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (getStoredTheme() === "system") {
      applyTheme();
    }
  });
};

/** Moved to @serophin/shared (beside the CSP that allows it by hash); re-exported here for existing imports. */
export { THEME_INIT_SCRIPT } from "@serophin/shared";
